import { useEffect, useState } from 'react';
import {
  Plus, Save, Trash2, Tag, Eye, EyeOff, Calendar, Percent, Banknote,
} from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Textarea, Select, StatusLine,
} from '../components/ui';

type Promo = {
  id: string;
  code: string;
  description: string | null;
  display_label: string | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  max_uses: number | null;
  uses_count: number;
  valid_from: string;
  valid_until: string | null;
  is_active: boolean;
  is_public: boolean;
  club_id: string | null;
  venue_id: string | null;
};

/**
 * Club finance/owner staff manage their own promo codes here. RLS
 * keeps non-finance users out via the manages_finance() helper.
 *
 * is_public surfaces the code on the brand page so customers don't
 * need to hear about it elsewhere; private codes only redeem when
 * the customer types them at checkout.
 */
export default function ClubPromos({ clubId }: { clubId: string }) {
  const [rows, setRows] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Promo | 'new' | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    // Pull anything tied to this club either by club_id or via a venue
    // belonging to this club. The RLS write policy already applies the
    // same scoping; the SELECT policy is the existing public-anon read,
    // so we filter client-side to avoid leaking unrelated brand-wide
    // promos onto the partner page.
    const [byClub, venues] = await Promise.all([
      supabase.from('promo_codes').select('*').eq('club_id', clubId),
      (supabase.from as any)('club_venues').select('id').eq('club_id', clubId),
    ]);
    const venueIds = ((venues.data ?? []) as Array<{ id: string }>).map((v) => v.id);
    let byVenue: any = { data: [] };
    if (venueIds.length > 0) {
      byVenue = await supabase.from('promo_codes').select('*').in('venue_id', venueIds);
    }
    const merged: Promo[] = [];
    const seen = new Set<string>();
    for (const row of [...(byClub.data ?? []), ...(byVenue.data ?? [])] as Promo[]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      merged.push(row);
    }
    merged.sort((a, b) => (b.valid_from ?? '').localeCompare(a.valid_from ?? ''));
    setLoading(false);
    setRows(merged);
  };

  useEffect(() => { load(); }, [clubId]);

  const setActive = async (p: Promo, is_active: boolean) => {
    setMsg(null);
    const { error } = await supabase.from('promo_codes').update({ is_active }).eq('id', p.id);
    if (error) setMsg({ kind: 'error', text: error.message });
    else { load(); setMsg({ kind: 'success', text: `${p.code} ${is_active ? 'enabled' : 'disabled'}.` }); }
  };

  const setPublic = async (p: Promo, is_public: boolean) => {
    setMsg(null);
    const { error } = await (supabase.from as any)('promo_codes').update({ is_public }).eq('id', p.id);
    if (error) setMsg({ kind: 'error', text: error.message });
    else { load(); setMsg({ kind: 'success', text: `${p.code} ${is_public ? 'shown on brand page' : 'hidden from brand page'}.` }); }
  };

  const remove = async (p: Promo) => {
    if (!confirm(`Delete promo code ${p.code}? This can't be undone.`)) return;
    setMsg(null);
    const { error } = await supabase.from('promo_codes').delete().eq('id', p.id);
    if (error) setMsg({ kind: 'error', text: error.message });
    else { load(); setMsg({ kind: 'success', text: 'Deleted.' }); }
  };

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Club"
        title="Promotions"
        subtitle="Discount codes for your club. Mark a code as Public to surface it on your brand page so customers don't need to hear about it elsewhere."
        actions={<Button icon={Plus} onClick={() => setEditing('new')}>New promotion</Button>}
      />

      {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading promotions…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Tag}
            title="No promotions yet"
            description="Create one to give regulars a discount or run a launch offer."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Code</th>
                <th className="text-left px-5 py-3 font-semibold">Discount</th>
                <th className="text-left px-5 py-3 font-semibold">Valid</th>
                <th className="text-left px-5 py-3 font-semibold">Uses</th>
                <th className="text-left px-5 py-3 font-semibold">Visibility</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const expiresOn = p.valid_until
                  ? new Date(p.valid_until).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                  : null;
                const isExpired = !!p.valid_until && new Date(p.valid_until).getTime() < Date.now();
                return (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-5 py-3">
                      <div className="font-mono font-semibold text-[#2d5016]">{p.code}</div>
                      {p.display_label && (
                        <div className="text-xs text-gray-700 mt-0.5">{p.display_label}</div>
                      )}
                      {p.description && (
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-1 max-w-md">{p.description}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone="lime" icon={p.discount_type === 'percentage' ? Percent : Banknote}>
                        {p.discount_type === 'percentage'
                          ? `${p.discount_value}% off`
                          : `AED ${Number(p.discount_value).toFixed(0)} off`}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-700">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        {expiresOn ? `Until ${expiresOn}` : 'No expiry'}
                      </div>
                      {isExpired && (
                        <div className="text-[11px] text-red-700 mt-0.5">Expired</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-700">
                      {p.uses_count}{p.max_uses != null ? ` / ${p.max_uses}` : ''}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1.5">
                        <button
                          type="button"
                          onClick={() => setActive(p, !p.is_active)}
                          className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${
                            p.is_active
                              ? 'bg-green-50 border-green-200 text-green-800'
                              : 'bg-white border-gray-200 text-gray-500'
                          }`}
                        >
                          {p.is_active ? 'Active' : 'Disabled'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPublic(p, !p.is_public)}
                          className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${
                            p.is_public
                              ? 'bg-[#c6f806]/30 border-[#2d5016]/30 text-[#2d5016]'
                              : 'bg-white border-gray-200 text-gray-500'
                          }`}
                        >
                          {p.is_public ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          {p.is_public ? 'On brand page' : 'Code-only'}
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setEditing(p)}>Edit</Button>
                        <Button size="sm" variant="danger" icon={Trash2} onClick={() => remove(p)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      {editing && (
        <PromoForm
          clubId={clubId}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function PromoForm({
  clubId, existing, onClose, onSaved,
}: {
  clubId: string;
  existing: Promo | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !existing;
  const [code, setCode] = useState(existing?.code ?? '');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>(existing?.discount_type ?? 'percentage');
  const [discountValue, setDiscountValue] = useState(existing?.discount_value != null ? String(existing.discount_value) : '');
  const [displayLabel, setDisplayLabel] = useState(existing?.display_label ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [maxUses, setMaxUses] = useState(existing?.max_uses != null ? String(existing.max_uses) : '');
  const [validFrom, setValidFrom] = useState(
    existing?.valid_from ? existing.valid_from.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [validUntil, setValidUntil] = useState(existing?.valid_until ? existing.valid_until.slice(0, 10) : '');
  const [isActive, setIsActive] = useState(existing?.is_active ?? true);
  const [isPublic, setIsPublic] = useState(existing?.is_public ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const dv = Number(discountValue);
    if (!code.trim() || isNaN(dv) || dv <= 0) {
      setBusy(false); setErr('Code and a positive discount value are required.'); return;
    }
    if (discountType === 'percentage' && dv > 100) {
      setBusy(false); setErr('Percentage discount cannot exceed 100%.'); return;
    }
    const payload: any = {
      code: code.trim().toUpperCase(),
      discount_type: discountType,
      discount_value: dv,
      display_label: displayLabel.trim() || null,
      description: description.trim() || null,
      max_uses: maxUses ? Number(maxUses) : null,
      valid_from: new Date(validFrom).toISOString(),
      valid_until: validUntil ? new Date(validUntil).toISOString() : null,
      is_active: isActive,
      is_public: isPublic,
      club_id: clubId,
    };
    const q = isNew
      ? supabase.from('promo_codes').insert(payload)
      : supabase.from('promo_codes').update(payload).eq('id', existing!.id);
    const { error } = await q;
    setBusy(false);
    if (error) setErr(error.message);
    else onSaved();
  };

  return (
    <Modal
      title={isNew ? 'New promotion' : `Edit ${existing!.code}`}
      subtitle="Customers redeem the code at checkout."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code (auto-uppercased)">
            <Input value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="WELCOME20" autoFocus />
          </Field>
          <Field label="Discount type">
            <Select value={discountType} onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed')}>
              <option value="percentage">Percentage off</option>
              <option value="fixed">Fixed AED off</option>
            </Select>
          </Field>
        </div>

        <Field label={discountType === 'percentage' ? 'Discount (%)' : 'Discount (AED)'}>
          <Input type="number" min={0} step="any" value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            placeholder={discountType === 'percentage' ? '20' : '50'} />
        </Field>

        <Field label="Headline (shown on brand page)">
          <Input value={displayLabel} onChange={(e) => setDisplayLabel(e.target.value)}
            placeholder="20% off your first booking" />
        </Field>

        <Field label="Description (optional)">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
            rows={2} placeholder="Terms, restrictions, who it's for." />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Max uses (blank = unlimited)">
            <Input type="number" min={1} value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)} placeholder="100" />
          </Field>
          <Field label="Valid from">
            <Input type="date" value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)} />
          </Field>
          <Field label="Valid until (optional)">
            <Input type="date" value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <Select value={isActive ? '1' : '0'} onChange={(e) => setIsActive(e.target.value === '1')}>
              <option value="1">Active — code redeems at checkout</option>
              <option value="0">Disabled — won't redeem</option>
            </Select>
          </Field>
          <Field label="Brand page visibility">
            <Select value={isPublic ? '1' : '0'} onChange={(e) => setIsPublic(e.target.value === '1')}>
              <option value="1">Public — show on brand page</option>
              <option value="0">Private — code-only</option>
            </Select>
          </Field>
        </div>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {isNew ? 'Create promotion' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
